import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Wallet, TrendingUp, TrendingDown, Lock } from 'lucide-react';
import PeriodFilter, { applyPeriodFilter, periodToRange } from '../components/PeriodFilter';
import DownloadFinancePdfButton from '../components/DownloadFinancePdfButton';

const formatCR = (n) => {
  const v = Math.round(parseFloat(n) || 0);
  return v.toLocaleString('fr-FR');
};
const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];

export default function TresorerieInvestisseur({ user, onLogout }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const year = new Date().getFullYear();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/financial/tresorerie?year=${year}`);
      setData(res.data);
      const dashRes = await axios.get(`${API}/api/financial/dashboard`);
      setData((prev) => ({
        ...prev,
        ...res.data,
        rawCredits: dashRes.data.credits || [],
        rawDebits: dashRes.data.debits || [],
      }));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [API, year]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  const {
    solde_initial, encaissements_par_categorie, decaissements_par_categorie, flux_mensuel,
    rawCredits = [], rawDebits = [],
  } = data;

  // Calcul filtré selon la période sélectionnée
  const filteredCredits = applyPeriodFilter(rawCredits, period);
  const filteredDebits = applyPeriodFilter(rawDebits, period);
  const total_encaissements = period === 'all'
    ? (data.total_encaissements || 0)
    : filteredCredits.reduce((s, r) => s + (parseFloat(r.montant) || 0), 0);
  const total_decaissements = period === 'all'
    ? (data.total_decaissements || 0)
    : filteredDebits.reduce((s, r) => s + (parseFloat(r.montant) || 0), 0);
  const solde_final = period === 'all'
    ? (data.solde_final || 0)
    : solde_initial + total_encaissements - total_decaissements;
  const isPositif = solde_final >= 0;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="tresorerie-investisseur-page">
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={() => navigate(-1)} variant="outline" className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Button>
          <DownloadFinancePdfButton
            type="tresorerie"
            {...(() => {
              const r = periodToRange(period);
              if (r.date_debut) return r;
              return { date_debut: `${year}-01-01`, date_fin: `${year}-12-31` };
            })()}
            testId="tresorerie-inv-download-pdf"
          />
          <div className="flex-1">
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Wallet className="h-7 w-7 text-blue-600" /> Tableau de Trésorerie
            </h1>
            <p className="text-gray-600 text-sm">Flux de trésorerie — {year}</p>
          </div>
          <Badge variant="outline" className="gap-1 border-gray-300 text-gray-600 bg-gray-50">
            <Lock className="h-3 w-3" /> Accès en lecture seule
          </Badge>
          <PeriodFilter value={period} onChange={setPeriod} testIdPrefix="tresorerie-period" />
        </div>

        {/* KPI cards — cliquables */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            type="button"
            onClick={() => navigate('/tresorerie-investisseur/encaissements')}
            className="text-left"
            data-testid="enc-card"
          >
            <Card className="border-l-4 border-l-green-500 bg-gradient-to-br from-green-50 to-white hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <p className="text-xs uppercase font-semibold text-green-700 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3" /> Encaissements
                </p>
                <p className="text-xl font-bold text-green-700 mt-1">+{formatCR(total_encaissements)} FCFA</p>
                <p className="text-xs text-green-600 mt-1">Voir les détails →</p>
              </CardContent>
            </Card>
          </button>
          <button
            type="button"
            onClick={() => navigate('/tresorerie-investisseur/decaissements')}
            className="text-left"
            data-testid="dec-card"
          >
            <Card className="border-l-4 border-l-red-500 bg-gradient-to-br from-red-50 to-white hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <p className="text-xs uppercase font-semibold text-red-700 flex items-center gap-1">
                  <TrendingDown className="h-3 w-3" /> Décaissements
                </p>
                <p className="text-xl font-bold text-red-700 mt-1">−{formatCR(total_decaissements)} FCFA</p>
                <p className="text-xs text-red-600 mt-1">Voir les détails →</p>
              </CardContent>
            </Card>
          </button>
          <button
            type="button"
            onClick={() => navigate('/tresorerie-investisseur/evolution')}
            className="text-left"
            data-testid="solde-final-card"
          >
            <Card className={`border-l-4 ${isPositif ? 'border-l-blue-600 bg-gradient-to-br from-blue-50 to-white' : 'border-l-red-700 bg-gradient-to-br from-red-50 to-white'} hover:shadow-md transition-shadow cursor-pointer`}>
              <CardContent className="p-4">
                <p className={`text-xs uppercase font-semibold ${isPositif ? 'text-blue-700' : 'text-red-700'}`}>
                  Solde final
                </p>
                <p className={`text-xl font-bold mt-1 ${isPositif ? 'text-blue-700' : 'text-red-700'}`}>
                  {formatCR(solde_final)} FCFA
                </p>
                <p className={`text-xs mt-1 ${isPositif ? 'text-blue-600' : 'text-red-600'}`}>
                  Voir évolution mensuelle →
                </p>
              </CardContent>
            </Card>
          </button>
        </div>

        {/* Bilateral flux */}
        <Card className="shadow-lg border-2 border-blue-100">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-6 w-6" /> Flux de trésorerie — {year}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Décaissements gauche */}
              <div className="border-r border-gray-200">
                <div className="bg-red-600 text-white px-6 py-3">
                  <h3 className="font-bold text-lg uppercase tracking-wide flex items-center gap-2">
                    <TrendingDown className="h-5 w-5" /> Décaissements (Sorties)
                  </h3>
                </div>
                <table className="w-full">
                  <thead className="bg-red-50 border-b border-red-200">
                    <tr>
                      <th className="px-6 py-2 text-left text-xs font-semibold text-red-800 uppercase tracking-wider">Catégorie</th>
                      <th className="px-6 py-2 text-right text-xs font-semibold text-red-800 uppercase tracking-wider">Montant (FCFA)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-100">
                    {decaissements_par_categorie.length === 0 ? (
                      <tr>
                        <td className="px-6 py-3 text-gray-400 italic">Aucun décaissement</td>
                        <td className="px-6 py-3 text-right text-gray-400">0</td>
                      </tr>
                    ) : (
                      decaissements_par_categorie.map((g) => (
                        <React.Fragment key={`dec-${g.categorie}`}>
                          <tr className="bg-red-100/70">
                            <td colSpan={2} className="px-4 py-2 text-xs font-bold text-red-900 uppercase tracking-wide">{g.categorie}</td>
                          </tr>
                          {g.par_mois.map((v, i) => (v > 0 ? (
                            <tr key={i}><td className="px-6 py-2 pl-10 text-gray-700 text-sm">{MONTHS[i]} {year}</td>
                              <td className="px-6 py-2 text-right text-red-700 font-medium">{formatCR(v)}</td></tr>
                          ) : null))}
                          <tr className="bg-red-50/60 border-t border-red-200">
                            <td className="px-6 py-2 pl-10 text-xs italic text-red-800">Total {g.categorie}</td>
                            <td className="px-6 py-2 text-right text-red-800 font-bold">{formatCR(g.total)}</td>
                          </tr>
                        </React.Fragment>
                      ))
                    )}
                    {isPositif && solde_final > 0 && (
                      <tr className="bg-blue-50 border-t border-blue-200">
                        <td className="px-6 py-3 italic text-blue-700 font-medium">Excédent de trésorerie</td>
                        <td className="px-6 py-3 text-right text-blue-700 font-bold">{formatCR(solde_final)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Encaissements droite */}
              <div>
                <div className="bg-green-600 text-white px-6 py-3">
                  <h3 className="font-bold text-lg uppercase tracking-wide flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" /> Encaissements (Entrées)
                  </h3>
                </div>
                <table className="w-full">
                  <thead className="bg-green-50 border-b border-green-200">
                    <tr>
                      <th className="px-6 py-2 text-left text-xs font-semibold text-green-800 uppercase tracking-wider">Catégorie</th>
                      <th className="px-6 py-2 text-right text-xs font-semibold text-green-800 uppercase tracking-wider">Montant (FCFA)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-green-100">
                    {solde_initial > 0 && (
                      <>
                        <tr className="bg-gray-100/70"><td colSpan={2} className="px-4 py-2 text-xs font-bold text-gray-700 uppercase">Solde initial de trésorerie</td></tr>
                        <tr><td className="px-6 py-2 pl-10 text-gray-700 text-sm">Report au 1er janvier {year}</td>
                          <td className="px-6 py-2 text-right text-gray-700 font-medium">{formatCR(solde_initial)}</td></tr>
                      </>
                    )}
                    {encaissements_par_categorie.length === 0 ? (
                      <tr><td className="px-6 py-3 text-gray-400 italic">Aucun encaissement</td>
                        <td className="px-6 py-3 text-right text-gray-400">0</td></tr>
                    ) : (
                      encaissements_par_categorie.map((g) => (
                        <React.Fragment key={`enc-${g.categorie}`}>
                          <tr className="bg-green-100/70"><td colSpan={2} className="px-4 py-2 text-xs font-bold text-green-900 uppercase">{g.categorie}</td></tr>
                          {g.par_mois.map((v, i) => (v > 0 ? (
                            <tr key={i}><td className="px-6 py-2 pl-10 text-gray-700 text-sm">{MONTHS[i]} {year}</td>
                              <td className="px-6 py-2 text-right text-green-700 font-medium">{formatCR(v)}</td></tr>
                          ) : null))}
                          <tr className="bg-green-50/60 border-t border-green-200">
                            <td className="px-6 py-2 pl-10 text-xs italic text-green-800">Total {g.categorie}</td>
                            <td className="px-6 py-2 text-right text-green-800 font-bold">{formatCR(g.total)}</td>
                          </tr>
                        </React.Fragment>
                      ))
                    )}
                    {!isPositif && solde_final < 0 && (
                      <tr className="bg-orange-50 border-t border-orange-200">
                        <td className="px-6 py-3 italic text-orange-700 font-medium">Déficit de trésorerie</td>
                        <td className="px-6 py-3 text-right text-orange-700 font-bold">{formatCR(Math.abs(solde_final))}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 bg-gradient-to-r from-red-100 via-gray-100 to-green-100 border-t-2 border-b-2 border-gray-300 font-bold">
              <div className="px-6 py-4 flex items-center justify-between border-r border-gray-300">
                <span className="text-red-900 uppercase tracking-wide">Total décaissements</span>
                <span className="text-red-900 text-xl">{formatCR(total_decaissements + (isPositif ? solde_final : 0))} FCFA</span>
              </div>
              <div className="px-6 py-4 flex items-center justify-between">
                <span className="text-green-900 uppercase tracking-wide">Total encaissements</span>
                <span className="text-green-900 text-xl">{formatCR(total_encaissements + solde_initial + (!isPositif ? Math.abs(solde_final) : 0))} FCFA</span>
              </div>
            </div>
            <div className={`px-6 py-5 text-center font-bold text-lg ${isPositif ? 'bg-blue-600 text-white' : 'bg-red-700 text-white'}`}>
              Solde final de trésorerie : {formatCR(solde_final)} FCFA {isPositif ? '(Excédent)' : '(Déficit)'}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
